import { yupResolver } from '@hookform/resolvers/yup';
import * as Dialog from '@radix-ui/react-dialog';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { AxiosError, AxiosInstance } from 'axios';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AiOutlineDownload, AiOutlineUpload } from 'react-icons/ai';
import { RxCross2 } from 'react-icons/rx';
import * as Yup from 'yup';
import { create } from 'zustand';
import { errorMessageAdapter } from '../../adapters';
import { MainCustomButton } from '../../components/Buttons';
import { ListCompanyDocuments } from './CompanyDocuments/ListCompanyDocuments';
import i18n from '../../plugins/i18n/i18n';
import { DocumentService } from '../../services/DocumentService';
import { ManagementService } from '../../services/ManagementService';
import { useAuth } from '../../store/auth';
import { downloadFile } from '../../utils/downloadFile';
import { hasFileName, validateFileSize, validateFileType } from '../../utils/fileValidation';
import formatDate from '../../utils/formatData';
import { errorToast } from '../../utils/toasts';
import { Card, PageHeadline } from '../../components/atoms';
import { useApi } from '../../services/hooks/apiHook';
import { ButtonActionRow } from './CompanyDocuments/styleGuide';
import { useAppSelector } from '../../hooks/hooksRedux';
import { RootState } from '../../store';
import { defaultBorder, defaultColors } from '../../styles/style-guide';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { DashboardCustomerContext } from './Context';

interface DialogsState {
  open: boolean;
  setOpen: (open: boolean) => void;
  actionType: number;
  setActionType: (actionType: number) => void;
  shareholderSlug: string;
  setShareholderSlug: (shareholderSlug: string) => void;
  documentSlug: string;
  setDocumentSlug: (documentSlug: string) => void;
}

interface IFormDocument {
  document: FileList;
}

const useDialogsStore = create<DialogsState>((set: any) => ({
  open: false,
  setOpen: (open: boolean) => set({ open }),
  actionType: 0,
  setActionType: (actionType: number) => set({ actionType }),
  shareholderSlug: '',
  setShareholderSlug: (shareholderSlug: string) => set({ shareholderSlug }),
  documentSlug: '',
  setDocumentSlug: (documentSlug: string) => set({ documentSlug })
}));

// ["pending", "approved", "rejected", "reproved", "waiting for more info"]
export const CompanyStatusColumn: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  
  const getStatusConfig = (status: string) => {
    const statusMap: { [key: string]: { text: string; bgColor: string; textColor: string } } = {
      'reproved': {
        text: t('others.othNotApproved'),
        bgColor: 'bg-red-50',
        textColor: 'text-red-600'
      },
      'rejected': {
        text: t('others.othNotApproved'),
        bgColor: 'bg-red-50',
        textColor: 'text-red-600'
      },
      'approved': {
        text: t('others.othApproved'),
        bgColor: 'bg-green-50',
        textColor: 'text-green-600'
      },
      'waiting for more info': {
        text: t('others.othWaitingForMoreInfo'),
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-600'
      },
      'pending': {
        text: t('others.othPending'),
        bgColor: 'bg-purple-50',
        textColor: 'text-purple-600'
      }
    };
    return statusMap[status] || statusMap['pending'];
  };

  const config = getStatusConfig(status);

  return (
    <td className="px-4 py-4">
      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
        {config.text}
      </span>
    </td>
  );
};

const DialogSendDocument: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const api: AxiosInstance = useApi();
  const { open, setOpen, actionType, shareholderSlug, documentSlug } = useDialogsStore();
  const [fileNameDocument, setFileNameDocument] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const validationSchema = Yup.object().shape({
    document: Yup.mixed()
      .required(t('Required') || '')
      .test('required', t('Required') || '', hasFileName)
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType)
  });
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset
  } = useForm<IFormDocument>({
    resolver: yupResolver(validationSchema)
  });
  const handleFileChangeDocument = (e: any) => {
    if (e.target.files.length > 0) {
      setFileNameDocument(e.target.files[0].name);
      setValue('document', e.target.files[0]);
    }
  };

  const onSubmit: SubmitHandler<IFormDocument> = async (formData) => {
    try {
      setLoading(true);
      const { data } = await DocumentService.resendDocument(
        api,
        {
          shareholder_slug: shareholderSlug,
          document_slug: documentSlug,
          action: actionType,
          document: formData.document
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Language': i18n.language.toLowerCase(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setLoading(false);
      setOpen(false);
      setFileNameDocument(null);
      reset();
    } catch (e) {
      setOpen(false);
      setLoading(false);
      setFileNameDocument(null);
      reset();
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-20 data-[state=open]:animate-overlayShow " />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white py-6 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
          <Dialog.Title className="m-0 text-[17px] font-medium text-black">
            <h1 className="px-4">Resend Document</h1>
            <Separator className="my-[15px] bg-slate-200 data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
          </Dialog.Title>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-4">
              <label className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center rounded border-[1px] border-dashed border-[#0C1FAA] bg-[#E2E5FA]/30 hover:bg-[#E2E5FA]">
                <img src="/svg/upload.svg" alt="upload" />
                <span className="text-lg font-bold text-[#0C1FAA]">{t('labels.lblPushYourDocument')}</span>
                <span className="text text-[#0C1FAA]">{t('labels.lblOrSearchInYourComputer')}</span>
                <span className="text-xs text-[#0C1FAA]">{t('labels.lblDocumentsSupported')} </span>
                <input
                  id="document"
                  type="file"
                  className="hidden"
                  {...register('document')}
                  onChange={handleFileChangeDocument}
                />
                {errors.document?.message && <span className="text-red-500">{errors.document.message}</span>}
              </label>
              {fileNameDocument && <p>{fileNameDocument}</p>}
            </div>
            <Separator className="my-[15px] bg-slate-200 data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
            <div className="mt-[25px] grid grid-cols-3 gap-8 px-4">
              <Dialog.Close asChild>
                <button className="flex items-center justify-center rounded-md border border-[#0C1FAA] text-sm font-medium text-[#0C1FAA]">
                  {t('others.othCancel')}
                </button>
              </Dialog.Close>
              <MainCustomButton text="Send" loading={loading} type="submit" />
            </div>
          </form>
          <Dialog.Close asChild>
            <button
              className="absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full text-violet11 hover:bg-violet4 focus:shadow-[0_0_0_2px] focus:shadow-violet7 focus:outline-none"
              aria-label="Close"
            >
              <RxCross2 />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
const CompanyAction: React.FC<{ document: any; actionType: number; shareholderSlug: string }> = ({
  document,
  actionType,
  shareholderSlug
}) => {
  const { token } = useAuth();
  const { t, i18n } = useTranslation();
  const { open, setOpen, setActionType, setShareholderSlug, setDocumentSlug } = useDialogsStore();

  const { colors, border } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;
  const borderPrimaryColor = border.primary || defaultBorder.primary;

  const documentUrl: string = document.prove_address_url || document.document_url;
  const api: AxiosInstance = useApi();

  const handleDownload = async () => {
    const { links } = await ManagementService.getDocumentUrl(
      api,
      {
        arquivos: [
          {
            path: documentUrl
          }
        ]
      },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Language': i18n.language.toLowerCase() } }
    );

    downloadFile(links[0].link, 'file.pdf');
  };

  if (document.status === 'reproved') {
    return (
      <button
        onClick={() => {
          setOpen(!open);
          setActionType(actionType);
          setShareholderSlug(shareholderSlug);
          setDocumentSlug(document.slug);
        }}
        className="inline-flex items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
        style={{ borderColor: borderPrimaryColor, color: primaryColor }}
      >
        <AiOutlineUpload size={16} />
        Re-upload
      </button>
    );
  }

  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-2 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
      style={{ borderColor: borderPrimaryColor, color: primaryColor }}
    >
      <AiOutlineDownload size={16} />
      {t('labels.download')}
    </button>
  );
};

const TableRow: React.FC<{ document: any; actionType: number; shareholderSlug: string; documentType: string; shareholderName: string; isDirector: any }> = ({
  document,
  actionType,
  shareholderSlug,
  documentType,
  shareholderName,
  isDirector
}) => {
  const { t } = useTranslation();
  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const primaryColor = colors.primary || defaultColors.primary;
  
  // Map document type to readable names
  const getDocumentTypeName = (type: string) => {
    const typeMap: { [key: string]: string } = {
      address: t('documents.proofOfAddress'),
      passport: t('documents.passport'),
      document: t('documents.localDocument')
    };
    return typeMap[type] || type;
  };

  // Build document name
  const documentName = document.description || document.document_name || '';
  const fullDocumentName = documentName 
    ? `${getDocumentTypeName(documentType)} - ${documentName}`
    : getDocumentTypeName(documentType);

  // Check if is director
  const isDirectorValue = isDirector === 1 || isDirector === true;
  const personType = isDirectorValue ? t('labels.lblDirector') || 'Director' : t('labels.lblShareholder') || 'Shareholder';
  
  return (
    <tr className="border-b border-[#E1E9EF] transition-colors hover:bg-[#F8F9FA] last:border-b-0">
      <td className="px-4 py-4">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-[#373D43]" style={{color: primaryColor}}>
            {fullDocumentName}
          </span>
          <span className="text-xs text-gray-500">
            {personType}: {shareholderName}
          </span>
        </div>
      </td>

      <td className="px-4 py-4">
        {document.message ? (
          <span className="text-sm text-gray-500">
            {document.message.length > 40 ? document.message.substring(0, 40) + '...' : document.message}
          </span>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )}
      </td>

      <td className="px-4 py-4">
        <span className="text-sm uppercase text-gray-600">{document.type}</span>
      </td>

      <td className="px-4 py-4">
        <span className="text-sm text-gray-600">{formatDate(document.created_at)}</span>
      </td>

      <CompanyStatusColumn status={document.status} />

      <td className="px-4 py-4">
        <CompanyAction document={document} actionType={actionType} shareholderSlug={shareholderSlug} />
      </td>
    </tr>
  );
};

const DashboardAnalyzeDocuments: React.FunctionComponent = () => {
  const { company } = useAuth();
  const { t } = useTranslation();
  const { open } = useDialogsStore();
  const { companyInfo, refreshCompanyInfo } = useContext(DashboardCustomerContext);
  const hasInitializedForceRefresh = useRef(false);

  useEffect(() => {
    if (!company?.id) {
      return;
    }

    if (!companyInfo) {
      void refreshCompanyInfo();
    }
  }, [company?.id, companyInfo, refreshCompanyInfo]);

  useEffect(() => {
    if (!company?.id) {
      return;
    }

    if (!hasInitializedForceRefresh.current) {
      hasInitializedForceRefresh.current = true;
      return;
    }

    if (!open) {
      void refreshCompanyInfo(true);
    }
  }, [open, company?.id, refreshCompanyInfo]);

  const shareholders = useMemo(() => companyInfo?.shareholders ?? [], [companyInfo?.shareholders]);

  return (
    <div className="w-full">
      <DialogSendDocument />
      <PageHeadline title={t('labels.lblAnalyzeDocuments')} />

      <Card.Root className="mt-8 w-full">
        <Card.Header className="font-medium text-lg">
          {t('labels.lblGeneralDocuments')}
        </Card.Header>
        
        <Card.Content className="w-full">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-full">
              <thead>
                <tr className="border-b border-[#E1E9EF]">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t('labels.lblDocumentName')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t('labels.lblMessageStatus')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t('labels.lblType')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t('labels.lblDatePosted')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t('labels.lblStatus')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{t('labels.lblActions')}</th>
                </tr>
              </thead>

              <tbody>
                {shareholders.length > 0 ? (
                  shareholders.map((shareholder: any, index: number) => (
                    <React.Fragment key={index}>
                      {shareholder.documents?.map((document: any) => (
                        <TableRow
                          document={document}
                          key={document.id}
                          shareholderSlug={shareholder.slug}
                          actionType={1}
                          documentType="document"
                          shareholderName={shareholder.name}
                          isDirector={shareholder.is_director}
                        />
                      ))}

                      {shareholder.passports?.map((document: any) => (
                        <TableRow
                          document={document}
                          key={document.id}
                          shareholderSlug={shareholder.slug}
                          actionType={3}
                          documentType="passport"
                          shareholderName={shareholder.name}
                          isDirector={shareholder.is_director}
                        />
                      ))}

                      {shareholder.address?.map((document: any) => (
                        <TableRow
                          document={document}
                          key={document.id}
                          shareholderSlug={shareholder.slug}
                          actionType={2}
                          documentType="address"
                          shareholderName={shareholder.name}
                          isDirector={shareholder.is_director}
                        />
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td className="pb-12 pt-12 text-center" colSpan={6}>
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <HiOutlineDocumentText className="mb-3" fontSize={48} />
                        <span className="font-medium">{t('labels.lblNoDataAvailable')}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card.Content>
      </Card.Root>
      
      {/* <ListCompanyDocuments data={companyInfo?.company_documents} /> */}
    </div>
  );
};

export { DashboardAnalyzeDocuments };
